import { Head, Link, router } from "@inertiajs/react"
import { Plus, Search, Wrench } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import {
  CarStatusBadge,
  QualDueBadge,
  RequalConflictBadge,
} from "@/components/products/fleet/badges"
import { EMPTY, humanize } from "@/components/products/fleet/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface NamedRef {
  id: number
  name: string
}

interface CarRow {
  id: number
  mark_number: string
  reporting_mark: string
  car_number: string
  fleet_code: string | null
  car_type: string | null
  status: string
  next_qual_due_on: string | null
  requal_conflict: boolean
  open_work_orders: number
  lessor: NamedRef | null
  rider: {
    id: number
    rider_number: string
    expiration_on: string | null
  } | null
  facility: NamedRef | null
}

interface Filters {
  q: string | null
  status: string | null
  car_type: string | null
  fleet_code: string | null
  lessor_id: string | null
  facility_id: string | null
  requal_within_months: string | null
}

interface Props {
  cars: CarRow[]
  filters: Filters
  filter_options: {
    fleet_codes: string[]
    car_types: string[]
    statuses: string[]
    lessors: NamedRef[]
    facilities: NamedRef[]
  }
  pagination: {
    page: number
    per_page: number
    total_count: number
    total_pages: number
  }
  can_edit: boolean
}

// Radix forbids an empty Select value, so "any" is the sentinel for "no filter"
// and is stripped from the query string on the way out.
const ANY = "any"

const REQUAL_WINDOWS = [
  { value: "3", label: "Due within 3 months" },
  { value: "6", label: "Due within 6 months" },
  { value: "12", label: "Due within 12 months" },
  { value: "24", label: "Due within 24 months" },
]

export default function CarsIndex({
  cars,
  filters,
  filter_options,
  pagination,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Cars", href: routes.carsPath() },
  ]

  const [query, setQuery] = useState(filters.q ?? "")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // FILTERING IS SERVER-SIDE. The fleet is thousands of rows carrying per-row
  // open-work-order counts, so the browser never holds the whole roster —
  // every change is a scoped re-query.
  function applyFilters(overrides: Partial<Filters>, delay = 0) {
    const next: Filters = { ...filters, q: query, ...overrides }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(next)) {
      if (value && value !== ANY) params[key] = String(value)
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      router.get(routes.carsPath(), params, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, delay)
  }

  function goToPage(page: number) {
    const params: Record<string, string> = { page: String(page) }
    for (const [key, value] of Object.entries(filters)) {
      if (value) params[key] = String(value)
    }
    router.get(routes.carsPath(), params, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const { page, total_pages, total_count } = pagination

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Cars" />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Cars</h1>
            <p className="text-muted-foreground text-sm">
              {total_count.toLocaleString()}{" "}
              {total_count === 1 ? "car" : "cars"} match these filters. A car is
              never deleted — retiring one sets its status to inactive.
            </p>
          </div>
          {can_edit && (
            <Button asChild>
              <Link href={routes.newCarPath()}>
                <Plus className="mr-1 size-4" /> Add car
              </Link>
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              className="text-muted-foreground absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search mark or car number"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                applyFilters({ q: e.target.value }, 400)
              }}
              className="h-9 pl-7"
              aria-label="Search cars"
            />
          </div>

          <FilterSelect
            label="Fleet"
            value={filters.fleet_code ?? ANY}
            options={filter_options.fleet_codes.map((code) => ({
              value: code,
              label: code,
            }))}
            allLabel="All fleets"
            onChange={(value) => applyFilters({ fleet_code: value })}
          />
          <FilterSelect
            label="Lessor"
            value={filters.lessor_id ?? ANY}
            options={filter_options.lessors.map((l) => ({
              value: String(l.id),
              label: l.name,
            }))}
            allLabel="All lessors"
            onChange={(value) => applyFilters({ lessor_id: value })}
          />
          <FilterSelect
            label="Facility"
            value={filters.facility_id ?? ANY}
            options={filter_options.facilities.map((f) => ({
              value: String(f.id),
              label: f.name,
            }))}
            allLabel="All facilities"
            onChange={(value) => applyFilters({ facility_id: value })}
          />
          <FilterSelect
            label="Status"
            value={filters.status ?? ANY}
            options={filter_options.statuses.map((s) => ({
              value: s,
              label: humanize(s),
            }))}
            allLabel="Any status"
            onChange={(value) => applyFilters({ status: value })}
          />
          <FilterSelect
            label="Type"
            value={filters.car_type ?? ANY}
            options={filter_options.car_types.map((t) => ({
              value: t,
              label: humanize(t),
            }))}
            allLabel="Any type"
            onChange={(value) => applyFilters({ car_type: value })}
          />
          <FilterSelect
            label="Requal"
            value={filters.requal_within_months ?? ANY}
            options={REQUAL_WINDOWS}
            allLabel="Any due date"
            onChange={(value) => applyFilters({ requal_within_months: value })}
          />
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Car</TableHead>
                <TableHead>Fleet</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Lessor / rider</TableHead>
                <TableHead>Facility</TableHead>
                <TableHead>Next qual due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Open WOs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cars.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-muted-foreground py-8 text-sm"
                  >
                    No cars match these filters. Clear a filter, or add a car to
                    the fleet.
                  </TableCell>
                </TableRow>
              ) : (
                cars.map((car) => (
                  <TableRow key={car.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          href={routes.carPath(car.id)}
                          className="font-medium hover:underline"
                        >
                          {car.mark_number}
                        </Link>
                        {car.requal_conflict && <RequalConflictBadge />}
                      </div>
                    </TableCell>
                    <TableCell>
                      {car.fleet_code ? (
                        <Badge variant="secondary" className="text-xs">
                          {car.fleet_code}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {humanize(car.car_type)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {car.lessor ? (
                        <div className="min-w-0">
                          <Link
                            href={routes.lessorPath(car.lessor.id)}
                            className="hover:underline"
                          >
                            {car.lessor.name}
                          </Link>
                          {car.rider && (
                            <div className="text-muted-foreground text-xs">
                              {car.rider.rider_number}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {car.facility?.name ?? (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <QualDueBadge dueOn={car.next_qual_due_on} />
                    </TableCell>
                    <TableCell>
                      <CarStatusBadge status={car.status} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {car.open_work_orders > 0 ? (
                        <span className="inline-flex items-center gap-1 font-medium">
                          <Wrench
                            className="text-muted-foreground size-3.5"
                            aria-hidden="true"
                          />
                          {car.open_work_orders}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm tabular-nums">
              Page {page} of {total_pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= total_pages}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

function FilterSelect({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  allLabel: string
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-9 w-[10rem] text-sm"
        aria-label={`Filter by ${label.toLowerCase()}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
